import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, TextField, Button, Grid, Divider, Alert } from '@mui/material';
import { Save, Palette } from '@mui/icons-material';
import { brandingAPI } from '../../services/api';
import { useNotify } from '../../utils/notify';

var DEFAULTS = {
  portal_name: 'OKC 2027',
  portal_tagline: 'Scholarship Portal',
  logo_url: '',
  email_header_bg: '#1a5276',
  email_header_text: '#ffffff',
  email_body_bg: '#f4f7fa',
  email_body_text: '#243b53',
  email_footer_text: '#8a97a8',
  email_link_color: '#1a5276',
  website_primary: '#1a5276',
  website_secondary: '#e67e22',
};

function ColorField(props) {
  var E = React.createElement;
  return E(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5 } },
    E('input', {
      type: 'color',
      value: props.value || '#000000',
      onChange: function(e) { props.onChange(e.target.value); },
      style: { width: 40, height: 36, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 2 }
    }),
    E(TextField, {
      size: 'small',
      label: props.label,
      value: props.value,
      onChange: function(e) { props.onChange(e.target.value); },
      sx: { flex: 1 },
      inputProps: { maxLength: 7 },
      helperText: props.helperText
    })
  );
}

function EmailPreview(props) {
  var b = props.branding;
  var E = React.createElement;

  var logoEl = null;
  if (b.logo_url) {
    logoEl = E('img', {
      src: b.logo_url, alt: b.portal_name,
      style: { maxHeight: 32, marginRight: 10, verticalAlign: 'middle' }
    });
  }

  return E('div', {
    style: {
      background: b.email_body_bg, padding: '20px 8px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      borderRadius: 8
    }
  },
    E('div', {
      style: {
        maxWidth: 480, margin: '0 auto', background: '#ffffff',
        border: '1px solid #e3e8ee', borderRadius: 10, overflow: 'hidden'
      }
    },
      E('div', { style: { background: b.email_header_bg, padding: '14px 20px' } },
        logoEl,
        E('span', {
          style: { color: b.email_header_text, fontSize: 16, fontWeight: 700, letterSpacing: 0.2 }
        }, b.portal_name || 'Portal Name')),
      E('div', {
        style: { padding: '20px', color: b.email_body_text, fontSize: 14, lineHeight: 1.6 }
      },
        E('p', { style: { margin: '0 0 12px' } }, 'Hello ', E('strong', null, 'Applicant Name'), ','),
        E('p', { style: { margin: '0 0 12px' } },
          'This is a preview of how your emails will look with the current branding settings.'),
        E('p', { style: { margin: '0 0 12px' } },
          'Your verification code is: ', E('strong', null, '847291')),
        E('p', { style: { margin: 0 } },
          E('a', { href: '#', style: { color: b.email_link_color } }, 'Click here to verify your application'))
      ),
      E('div', {
        style: {
          padding: '12px 20px', borderTop: '1px solid #eef2f6',
          color: b.email_footer_text, fontSize: 11
        }
      }, 'This message was sent by the ' + (b.portal_name || 'Portal') + ' portal. If it reached you unexpectedly, you can ignore it.')
    )
  );
}

export default function Branding() {
  var notify = useNotify();
  var cs = useState(Object.assign({}, DEFAULTS));
  var config = cs[0], setConfig = cs[1];
  var ls = useState(true);
  var loading = ls[0], setLoading = ls[1];
  var E = React.createElement;

  useEffect(function() {
    brandingAPI.get()
      .then(function(r) {
        if (r.data && r.data.data) {
          setConfig(Object.assign({}, DEFAULTS, r.data.data));
        }
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }, []);

  function update(field) {
    return function(val) {
      var patch = {};
      patch[field] = typeof val === 'object' && val.target ? val.target.value : val;
      setConfig(Object.assign({}, config, patch));
    };
  }

  function save() {
    brandingAPI.save(config)
      .then(function() { notify('Branding saved. Refresh the page to see website changes.', { variant: 'success' }); })
      .catch(function(err) {
        var msg = (err.response && err.response.data && err.response.data.error) || 'Failed to save';
        notify(msg, { variant: 'error' });
      });
  }

  function resetDefaults() {
    setConfig(Object.assign({}, DEFAULTS));
  }

  if (loading) return E(Box, { sx: { p: 4 } }, E(Typography, null, 'Loading...'));

  return E(Box, null,
    E(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 } },
      E(Typography, { variant: 'h4' }, 'Branding'),
      E(Box, { sx: { display: 'flex', gap: 1 } },
        E(Button, { variant: 'outlined', onClick: resetDefaults }, 'Reset to Defaults'),
        E(Button, { variant: 'contained', startIcon: E(Save), onClick: save }, 'Save Branding'))),

    E(Grid, { container: true, spacing: 3 },
      // Left column: settings
      E(Grid, { item: true, xs: 12, md: 6 },
        // Portal identity
        E(Card, { sx: { mb: 3 } },
          E(CardContent, null,
            E(Typography, { variant: 'h6', sx: { mb: 2 } },
              E(Palette, { sx: { mr: 1, verticalAlign: 'middle' } }), 'Portal Identity'),
            E(Grid, { container: true, spacing: 2 },
              E(Grid, { item: true, xs: 12 },
                E(TextField, { fullWidth: true, label: 'Portal Name', value: config.portal_name,
                  onChange: update('portal_name'),
                  helperText: 'Shown in email headers, admin sidebar, login page, and all layouts' })),
              E(Grid, { item: true, xs: 12 },
                E(TextField, { fullWidth: true, label: 'Portal Tagline', value: config.portal_tagline,
                  onChange: update('portal_tagline'),
                  helperText: 'Shown below portal name on the login page' })),
              E(Grid, { item: true, xs: 12 },
                E(TextField, { fullWidth: true, label: 'Logo URL', value: config.logo_url,
                  onChange: update('logo_url'),
                  helperText: 'Full URL to your logo image (shown in email header). Leave blank for text-only.' }))))),

        // Email branding
        E(Card, { sx: { mb: 3 } },
          E(CardContent, null,
            E(Typography, { variant: 'h6', sx: { mb: 2 } }, 'Email Branding'),
            E(Typography, { variant: 'body2', color: 'text.secondary', sx: { mb: 2 } },
              'These colors apply to ALL outgoing emails — OTP verification, decision notifications, templates, and test emails.'),
            E(Grid, { container: true, spacing: 2 },
              E(Grid, { item: true, xs: 12, sm: 6 },
                E(ColorField, { label: 'Header Background', value: config.email_header_bg,
                  onChange: update('email_header_bg') })),
              E(Grid, { item: true, xs: 12, sm: 6 },
                E(ColorField, { label: 'Header Text', value: config.email_header_text,
                  onChange: update('email_header_text') })),
              E(Grid, { item: true, xs: 12, sm: 6 },
                E(ColorField, { label: 'Body Background', value: config.email_body_bg,
                  onChange: update('email_body_bg') })),
              E(Grid, { item: true, xs: 12, sm: 6 },
                E(ColorField, { label: 'Body Text', value: config.email_body_text,
                  onChange: update('email_body_text') })),
              E(Grid, { item: true, xs: 12, sm: 6 },
                E(ColorField, { label: 'Footer Text', value: config.email_footer_text,
                  onChange: update('email_footer_text') })),
              E(Grid, { item: true, xs: 12, sm: 6 },
                E(ColorField, { label: 'Link Color', value: config.email_link_color,
                  onChange: update('email_link_color') }))))),

        // Website branding
        E(Card, null,
          E(CardContent, null,
            E(Typography, { variant: 'h6', sx: { mb: 2 } }, 'Website Branding'),
            E(Typography, { variant: 'body2', color: 'text.secondary', sx: { mb: 2 } },
              'Primary and secondary colors used across the admin panel, viewer portal, reviewer portal, and login page. Changes take effect after page refresh.'),
            E(Grid, { container: true, spacing: 2 },
              E(Grid, { item: true, xs: 12, sm: 6 },
                E(ColorField, { label: 'Primary Color', value: config.website_primary,
                  onChange: update('website_primary'),
                  helperText: 'App bar, buttons, sidebar' })),
              E(Grid, { item: true, xs: 12, sm: 6 },
                E(ColorField, { label: 'Secondary Color', value: config.website_secondary,
                  onChange: update('website_secondary'),
                  helperText: 'Accents, highlights' })))))),

      // Right column: preview
      E(Grid, { item: true, xs: 12, md: 6 },
        E(Card, null,
          E(CardContent, null,
            E(Typography, { variant: 'h6', sx: { mb: 2 } }, 'Email Preview'),
            E(Typography, { variant: 'body2', color: 'text.secondary', sx: { mb: 2 } },
              'Live preview of how outgoing emails will appear with your branding.'),
            E(EmailPreview, { branding: config }))),

        E(Alert, { severity: 'info', sx: { mt: 2 } },
          'Email branding applies immediately to all new emails. Website branding changes require a page refresh to take effect.'))));
}
